import { formatTimeOfDay, isoAttr } from "../../lib/datetime.ts";
import { type ReactNode } from "react";
import { ScreenScaffold, type ScreenState } from "../ScreenScaffold.tsx";
import { ChartSyncProvider } from "../../components/index.ts";
import { useIdentity, roleAtLeast } from "../../state/identity.tsx";
import { useTimeRange } from "../../state/time-range.tsx";
import { LivePauseControl } from "../../chrome/LivePauseControl.tsx";
import { TimeRangeControl } from "../../chrome/TimeRangeControl.tsx";
import {
  useEnrolmentFunnel,
  useIdentityTokens,
  useIdentityLifecycle,
  type EnrolmentFunnel as EnrolmentFunnelData,
  type IdentityWindow,
} from "./use-identity.ts";
import { EnrolmentFunnel } from "./EnrolmentFunnel.tsx";
import { TokenPanel } from "./TokenPanel.tsx";
import { LifecycleTimeline } from "./LifecycleTimeline.tsx";
import { EpochsPanel } from "./EpochsPanel.tsx";
import "./identity.css";

/**
 * The Identity lifecycle screen (task 11.7; design 12.2 Identity row). It composes the four regions
 * the design calls for over three REST reads, all role `viewer`:
 *
 * - `GET /api/v1/identity/enrolment-funnel` → the enrolment funnel (start → poll → activated →
 *   bound) with conversion and completed time-to-complete, and the stuck-pending block. This is the
 *   PRIMARY snapshot the screen's top-level state rests on.
 * - `GET /api/v1/identity/tokens` → token issuance over time and denial reasons.
 * - `GET /api/v1/identity/lifecycle` → the revoke/suspend/deprovision timeline AND the current key
 *   and revocation epochs.
 *
 * All three windows are resolved from the shared time-range, so pausing freezes every query at once
 * (design 12.2, FR-13 AC2). The funnel drives the scaffold state; the tokens and lifecycle regions
 * are secondary and render their own loading/empty/error state INSIDE the ready screen, so a slow or
 * empty tokens/lifecycle read never blanks the whole page.
 *
 * It handles every state a screen must (task 11 preamble; design 12.3):
 * - restricted: the funnel is viewer-level; an unknown/below-viewer role sees the restricted state.
 * - loading / error / empty / ready as the funnel query resolves; a cold-core waking error defers to
 *   the shell's banner (kept "loading" under it so there is no contradiction).
 */
export function IdentityScreen(): ReactNode {
  const { role } = useIdentity();
  const { live } = useTimeRange();

  const { query: funnel, window } = useEnrolmentFunnel();
  const { query: tokens } = useIdentityTokens();
  const { query: lifecycle } = useIdentityLifecycle();

  const state = resolveState(role, funnel);

  return (
    <ScreenScaffold
      title="Identity lifecycle"
      subtitle="Enrollment funnel with conversion and time-to-complete, stuck-pending age, token issuance and denials, the revoke/suspend/deprovision timeline, and key and revocation epochs."
      state={state}
    >
      {funnel.data !== undefined && (
        <IdentityBody
          funnel={funnel.data}
          window={window}
          live={live}
          tokens={tokens}
          lifecycle={lifecycle}
        />
      )}
    </ScreenScaffold>
  );
}

/** Decide which scaffold state to show from the role and the funnel query status. */
function resolveState(
  role: ReturnType<typeof useIdentity>["role"],
  query: ReturnType<typeof useEnrolmentFunnel>["query"],
): ScreenState {
  // Least privilege: an unknown or below-viewer role sees the restricted state, not an error.
  if (!roleAtLeast(role, "viewer")) {
    return {
      kind: "restricted",
      reason:
        role === "unknown"
          ? "Your role has not been resolved yet, so the identity view is hidden until it is. The server enforces access regardless."
          : "The identity view needs at least the viewer role.",
    };
  }

  if (query.data !== undefined) {
    if (isFunnelEmpty(query.data)) {
      return {
        kind: "empty",
        cause:
          "No enrollment, token or lifecycle activity was recorded in this window. Widen the time range, or check that the AIS is reporting identity events.",
      };
    }
    return { kind: "ready" };
  }

  if (query.isError) {
    // The cold-core waking error is the shell's banner; keep "loading" under it (no contradiction).
    if (query.error.isWaking) return { kind: "loading" };
    return { kind: "error", message: errorMessage(query.error) };
  }

  return { kind: "loading" };
}

/** The funnel is "empty" when no stage saw any enrolment and nothing is stuck. */
function isFunnelEmpty(data: EnrolmentFunnelData): boolean {
  const noStages = data.stages.every((s) => s.count === 0);
  const noTerminal = data.terminal_failures.every((s) => s.count === 0);
  const noneStuck = data.stuck_pending.count === 0;
  return noStages && noTerminal && noneStuck;
}

function errorMessage(error: ReturnType<typeof useEnrolmentFunnel>["query"]["error"]): string {
  if (error === null) return "The identity view could not load.";
  switch (error.kind) {
    case "unauthorized":
      return "Your session has expired. Reload to sign in again.";
    case "forbidden":
      return "You are not permitted to see identity data. The server enforces access.";
    case "network":
      return "The identity request could not be sent. Check your connection.";
    default:
      return error.message;
  }
}

function IdentityBody(props: {
  funnel: EnrolmentFunnelData;
  window: IdentityWindow;
  live: boolean;
  tokens: ReturnType<typeof useIdentityTokens>["query"];
  lifecycle: ReturnType<typeof useIdentityLifecycle>["query"];
}): ReactNode {
  const generatedAt = props.funnel.meta.generated_at;

  return (
    <div className="idn" data-live={props.live}>
      <div className="screen-meta-row">
        <LivePauseControl />
        <TimeRangeControl />
        <p className="idn__meta">
        {props.live ? "Live" : "Paused"} · window{" "}
        <time dateTime={isoAttr(props.window.fromMs)}>
          {formatTimeOfDay(props.window.fromMs)}
        </time>{" "}
        to{" "}
        <time dateTime={isoAttr(props.window.toMs)}>
          {formatTimeOfDay(props.window.toMs)}
        </time>{" "}
        · generated{" "}
        <time dateTime={isoAttr(generatedAt)}>
          {formatTimeOfDay(generatedAt)}
        </time>
      </p>
      </div>

      <EnrolmentFunnel data={props.funnel} />

      {/* Tokens is a secondary region: it shows its own state so a slow/empty read never blanks the
          page. The chart inside it joins a sync group of its own. */}
      <SecondaryRegion
        label="Tokens"
        isLoading={props.tokens.data === undefined && props.tokens.isFetching}
        isError={props.tokens.isError && !(props.tokens.error?.isWaking ?? false)}
        errorText="Token statistics could not load for this window."
      >
        {props.tokens.data !== undefined && (
          <ChartSyncProvider>
            <TokenPanel data={props.tokens.data} />
          </ChartSyncProvider>
        )}
      </SecondaryRegion>

      {/* Lifecycle timeline + the current epochs come from the one lifecycle page. */}
      <SecondaryRegion
        label="Lifecycle"
        isLoading={props.lifecycle.data === undefined && props.lifecycle.isFetching}
        isError={props.lifecycle.isError && !(props.lifecycle.error?.isWaking ?? false)}
        errorText="The lifecycle timeline could not load for this window."
      >
        {props.lifecycle.data !== undefined && (
          <>
            <EpochsPanel epochs={props.lifecycle.data.epochs} />
            <LifecycleTimeline page={props.lifecycle.data} />
          </>
        )}
      </SecondaryRegion>
    </div>
  );
}

/**
 * A secondary region wrapper: renders its children when the data is present, an honest loading
 * status while the first fetch is in flight, and a non-blocking error note when the (non-waking)
 * read failed — so one slow or failing secondary read never takes down the whole screen.
 */
function SecondaryRegion(props: {
  label: string;
  isLoading: boolean;
  isError: boolean;
  errorText: string;
  children?: ReactNode;
}): ReactNode {
  if (props.isLoading) {
    return (
      <div className="idn-secondary-state" role="status" aria-live="polite" aria-label={props.label}>
        <strong>Loading {props.label.toLowerCase()}…</strong>
      </div>
    );
  }
  if (props.isError) {
    return (
      <div className="idn-secondary-state" data-kind="error" role="alert" aria-label={props.label}>
        <strong>{props.label} unavailable</strong>
        <span>{props.errorText}</span>
      </div>
    );
  }
  return <>{props.children}</>;
}
